import {useEffect} from 'react'
import {useApolloClient} from '@apollo/client'
import {MY_PERMISSIONS} from '../gql/authz'
import {useGetUser} from '../store/userStore'

const sameSet = (a?: string[], b?: string[]) => {
  const A = new Set(a ?? [])
  const B = new Set(b ?? [])
  if (A.size !== B.size) return false
  return Array.from(A).every((x) => B.has(x))
}

// Refresh the current user's effective permissions/roles from the server on
// mount and on window focus, so RBAC changes (role edits, per-user overrides)
// take effect without forcing a re-login. Best-effort: on failure the
// login-time permissions are kept.
export const usePermissionSync = () => {
  const client = useApolloClient()
  const syncPermissions = useGetUser((s) => s.syncPermissions)
  const hasUser = useGetUser((s) => !!s.user)

  useEffect(() => {
    if (!hasUser) return
    let cancelled = false

    const refresh = async () => {
      try {
        const {data} = await client.query({query: MY_PERMISSIONS, fetchPolicy: 'network-only'})
        const mp = data?.myPermissions
        if (cancelled || !mp?.permissions) return
        const cur = useGetUser.getState().user
        if (!cur) return
        if (!sameSet(cur.permissions, mp.permissions) || !sameSet(cur.groups, mp.roles)) {
          syncPermissions(mp.permissions, mp.roles)
        }
      } catch {
        // keep existing permissions on failure
      }
    }

    refresh()
    const onFocus = () => refresh()
    window.addEventListener('focus', onFocus)
    return () => {
      cancelled = true
      window.removeEventListener('focus', onFocus)
    }
  }, [hasUser, client, syncPermissions])
}
